import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useServiceError } from '@/hooks';
import {
  createProfile,
  duplicateProfile,
  getProfile,
  getProfileReduced,
  listProfiles,
  listProfilesReduced,
  removeProfile,
  updateProfile,
} from './Profiles.service';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { store } from '@/Root';
import { authOperations } from '@/state/ducks/auth';
import { ProfileErrors } from './Profiles.errors';
import { ProfileReduced } from './Profiles.types';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useProfiles() {
  const query = useQuery(['profiles'], () => listProfiles());

  useServiceError(query.error);

  return query;
}

export function useProfilesReduced(enabled = true) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['profilesReduced']);

  return useQuery(['profilesReduced'], () => listProfilesReduced(), {
    onError(error) {
      notificationError(error);
    },
    enabled: enabled && !cache,
    cacheTime: 10000,
  });
}

export function useProfile(id: number) {
  const query = useQuery(['profiles', id], () => getProfile(id));

  useServiceError(query.error);

  return query;
}

export function useProfileReduced() {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<ProfileReduced>([
        'profile-detail-reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getProfileReduced(id);

      return data;
    },
    {
      onError(error) {
        notificationError(error);
      },
      onSuccess(data) {
        queryClient.setQueryData(['profile-detail-reduced', data.id], data);
      },
    },
  );
}

export function useCreateProfile() {
  const queryClient = useQueryClient();

  const mutation = useMutation(createProfile, {
    onSuccess(data) {
      queryClient.resetQueries(['profilesReduced']);

      notification.success({
        message: 'Cadastro de perfil',
        description: `Perfil "${data.name}" cadastrado com sucesso!`,
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => ProfileErrors[err] || err),
      });
    },
  });

  return mutation;
}

export function useDuplicateProfile() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(duplicateProfile, {
    onSuccess(data) {
      notification.success({
        message: `Grupo "${data.name}" replicado com sucesso!`,
      });

      queryChanel({
        resetQueries: [['profilesReduced']],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => ProfileErrors[err] || err),
      });
    },
  });

  return mutation;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const mutation = useMutation(updateProfile, {
    async onSuccess(data) {
      await store.dispatch(authOperations.handleRefreshToken());

      await queryClient.resetQueries(['profilesReduced']);

      notification.success({
        message: 'Atualização de perfil',
        description: `Perfil "${data.name}" atualizado com sucesso!`,
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useRemoveProfile() {
  const queryClient = useQueryClient();
  const mutation = useMutation(removeProfile, {
    onSuccess() {
      queryClient.resetQueries(['profilesReduced']);

      notification.success({
        message: 'Remoção de perfil',
        description: `Perfil removido com sucesso!`,
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Profile Referential integrity constraint violation')
              ? 'Não é possivel excluir o grupo de usuário, pois ele tem vínculo com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}
